import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/LoginPage.css";
import { verifyCode as verifyCodeApi } from "../api/auth";

const OTP_LENGTH = 6;
const DEFAULT_EXPIRES_IN_SECONDS = 5 * 60;

const formatTimer = (seconds) => {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`.replace(
    /\d/g,
    (digit) => Number(digit).toLocaleString("fa-IR")
  );
};

const VerifyCodePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const sentAtRef = useRef(location.state?.sentAt || Date.now());
  const expiresInSecondsRef = useRef(Number(location.state?.expiresInSeconds) || DEFAULT_EXPIRES_IN_SECONDS);
  const sentAt = sentAtRef.current;
  const expiresInSeconds = expiresInSecondsRef.current;

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [remainingSeconds, setRemainingSeconds] = useState(expiresInSeconds);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputsRef = useRef([]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    const updateRemaining = () => {
      const elapsedSeconds = Math.floor((Date.now() - sentAt) / 1000);
      setRemainingSeconds(Math.max(0, expiresInSeconds - elapsedSeconds));
    };

    updateRemaining();
    const timerId = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timerId);
  }, [expiresInSeconds, sentAt]);

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = digit;
    setOtp(nextOtp);
    if (digit && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    const nextOtp = Array(OTP_LENGTH)
      .fill("")
      .map((_, idx) => pasted[idx] || "");
    setOtp(nextOtp);
    const firstEmpty = nextOtp.findIndex((v) => !v);
    const focusIndex = firstEmpty === -1 ? OTP_LENGTH - 1 : firstEmpty;
    inputsRef.current[focusIndex]?.focus();
  };

  const handleSubmit = async () => {
    const code = otp.join("");
    if (code.length < OTP_LENGTH) {
      setError("کد تایید ۶ رقمی را کامل وارد کن.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await verifyCodeApi({ email, code });
      navigate("/login", { state: { verifiedEmail: email } });
    } catch (err) {
      setError(err.message || "کد تایید نادرست است.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="touch-wrapper" dir="rtl">
      {error && (
        <div className="touch-toast">
          <div>
            <div className="touch-toast-title">خطا در تایید کد</div>
            <p className="touch-toast-body">{error}</p>
          </div>
        </div>
      )}
      <div className="touch-left">
        <div className="touch-logo-wrapper">
          <img
            src="/images/touch-prop-logo.png"
            alt="Touch Prop Logo"
            className="touch-logo"
          />
        </div>

        <div className="touch-otp-container">
          <p className="touch-otp-text">
            کد تایید ارسال‌شده به <span className="touch-otp-email">{email}</span> را وارد کن
          </p>

          <div className="touch-otp-boxes" onPaste={handlePaste}>
            {otp.map((val, idx) => (
              <input
                key={idx}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={val}
                onChange={(e) => handleChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                ref={(el) => (inputsRef.current[idx] = el)}
                className="touch-otp-input"
              />
            ))}
          </div>

          <div className="touch-otp-timer">{formatTimer(remainingSeconds)}</div>

          <div className="touch-otp-resend">
            کد نرسیده؟{" "}
            <button type="button" className="touch-otp-resend-link" disabled>
              ارسال مجدد
            </button>
          </div>

          <button
            type="button"
            className="touch-primary-btn touch-otp-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "در حال بررسی..." : "تایید"}
          </button>
        </div>
      </div>

      <div className="touch-right">
        <img
          src="/images/fingerprint-pattern.png"
          alt="Fingerprint"
          className="touch-fingerprint"
        />
        <div className="touch-right-overlay">
          <div className="touch-hero-text">
            <h2 className="touch-hero-title">تاچ پراپ ، لمس ثروت بی کران</h2>
            <p className="touch-hero-body">
              مسیر معامله‌گری هوشمند از همین‌جا شروع می‌شه
            </p>
            <div className="touch-dots">
              <span className="touch-dot touch-dot-active" />
              <span className="touch-dot" />
              <span className="touch-dot" />
              <span className="touch-dot" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyCodePage;
